// The step timeline's geometry, plus the one function that consumes it
// (`timelineMinWidth`), pulled out of `Timeline.tsx` for two reasons:
//   (a) `timelineMinWidth` is a non-component FUNCTION export, and a second
//       export from a component file fails `react-refresh/only-export-components`.
//       Constants alone would NOT have forced this move — `app/eslint.config.js`'s
//       `reactRefresh.configs.vite` sets `allowConstantExport: true` — but the
//       constants belong beside the function that is the reason this module exists.
//   (b) Why the numbers matter at all: every node in the timeline is a plain flex
//       item (`s.nodeWrapper`) whose only child is a button with a definite CSS
//       `width`. A block box's min-content is its child's, and a child with a
//       definite width contributes exactly that width — so the row has a hard
//       min-content floor no `flex-shrink` can compress, and the Pipeline dialog
//       must be at least that floor plus its own chrome or a horizontal scrollbar
//       reappears. `pipeline-dialog-width.test.ts` pins the relationship.
import type { TimelineNode } from './constants'

export const NODE_W = 112
// 84 - 8 (NODE_PAD_Y * 2) - 40 (CIRCLE_D) - 6 (NODE_GAP) = 30px label box.
// Two 13.2px Caption1 lines (LABEL_LINE_H * 2 = 26.4) fit inside; one line held
// at the old NODE_H (70, giving a 16px label box) and any wrap spilled out of
// the button.
export const NODE_H = 84
export const CIRCLE_D = 40
export const NODE_PAD_Y = 4 // `s.node`'s `padding: '4px 0'`, one side
export const NODE_GAP = 6 // `s.node`'s `gap: '6px'` between circle and label
// Caption1 is Fluent's fontSizeBase200 (12px); `s.label` overrides line-height to 1.1.
export const LABEL_LINE_H = 12 * 1.1
// The circle's center, measured from the node's top. Unchanged at 24 — true
// only while `s.node` is `justifyContent: 'flex-start'`, which is why that
// property is now set explicitly rather than left to the button UA default.
export const CIRCLE_C = NODE_PAD_Y + CIRCLE_D / 2
export const BRANCH_GAP = 8
export const FORK_W = 28
export const FORK_H = NODE_H * 2 + BRANCH_GAP // 176
// A FLOOR only: `s.connector` is `flex: 1` and grows back above this width.
export const CONNECTOR_MIN_W = 8
export const DIVIDER_MARGIN = 12
export const DIVIDER_W = 2 + DIVIDER_MARGIN * 2 // the 2px rule plus both margins = 26

// The stuck-node warning badge (`s.stuckTrigger`). Derived, not guessed, so a
// change to NODE_W or CIRCLE_D moves the badge instead of silently sliding it
// off the circle's stroke: the badge sits at a fixed (BADGE_DX, BADGE_DY)
// offset from the circle's own center, and that offset is pinned by
// `pipeline-dialog-width.test.ts` to land exactly CIRCLE_D / 2 away — i.e. on
// the stroke — so a change to CIRCLE_D that is not matched by a change to
// BADGE_DX/BADGE_DY fails the build rather than drifting quietly.
export const BADGE_D = 18
export const BADGE_DX = 16
export const BADGE_DY = 12
// Circle center is (NODE_W / 2, CIRCLE_C). `right`/`top` are CSS offsets from
// the node's right/top edges to the badge's right/top edges.
export const BADGE_RIGHT = NODE_W / 2 - BADGE_DX - BADGE_D / 2 // 31 at NODE_W 112
export const BADGE_TOP = CIRCLE_C - BADGE_DY - BADGE_D / 2 // 3

export const PIPELINE_DIALOG_MAX_W = 1240
// 24px padding + 1px border on each side of `DialogSurface`. `DialogBody`
// adds none — `DialogContent` spans all three grid columns and both 8px gaps
// — and `DialogContent`'s own 2px padding is cancelled by its -2px margin.
export const DIALOG_SURFACE_CHROME = 50
export const PIPELINE_BODY_PAD_X = 8 // `s.pipelineBody`'s `padding`, both sides
export const PIPELINE_TIMELINE_W = PIPELINE_DIALOG_MAX_W - DIALOG_SURFACE_CHROME - PIPELINE_BODY_PAD_X // 1182

/**
 * The timeline row's min-content width: the narrowest the row can ever
 * render, given that every node is a fixed-width button. Mirrors
 * `Timeline.tsx`'s own row-building logic (`main` / `branch` / `fork` /
 * `archive` filters) rather than measuring the DOM, so the two can only
 * disagree about layout RULES, not about geometry.
 *
 * Two layout facts this encodes, both true of `Timeline.tsx` today:
 *   - the tail's nodes (Denial Review, Archives) carry NO connectors — each
 *     sits past its own `s.vDivider` instead, so the tail's width is divider
 *     widths plus node widths only, never `CONNECTOR_MIN_W`.
 *   - `s.connectorDashed` is unreachable on both render paths: `renderRow` is
 *     only ever called with branch-free arrays (`main`, `rightTop` — which is
 *     itself `.filter(n => !n.branch)` — and the singleton `[fork]`), so no
 *     row ever renders a dashed connector. A dashed and solid connector share
 *     the same `flex: 1`, so this would not change the arithmetic even if it
 *     were reachable, but it is why `CONNECTOR_MIN_W` alone is sufficient.
 */
export function timelineMinWidth(nodes: readonly TimelineNode[]): number {
  const chain = (n: number) => (n <= 0 ? 0 : n * NODE_W + (n - 1) * CONNECTOR_MIN_W)
  const main = nodes.filter((n) => !n.branch && !n.forkFrom && !n.archive)
  const branch = nodes.filter((n) => n.branch)
  const fork = nodes.find((n) => n.forkFrom)
  const archive = nodes.find((n) => n.archive)
  const tail =
    (branch.length > 0 ? DIVIDER_W + branch.length * NODE_W : 0) +
    (archive ? DIVIDER_W + NODE_W : 0)
  if (!fork) return chain(main.length) + tail
  const row = [...main, ...branch]
  const anchorIdx = row.findIndex((n) => n.key === fork.forkFrom)
  const rightTop = row.slice(anchorIdx + 1).filter((n) => !n.branch)
  return chain(anchorIdx + 1) + FORK_W + Math.max(chain(rightTop.length), chain(1)) + tail
}
